<template>
  <div class="tw-w-1/2">
    <div class="tw-flex tw-flex-row tw-justify-end" v-if="isLoading">
      <cl-spinner :loading-text="$t('loading')" :size="'small'" class="mr-2" />
    </div>
    <div v-else>
      <authorisation-wrapper
        :resource="ADMINISTRATORS"
        :action="WRITE"
        v-slot="{ isAllowed: isWriteAllowed }"
      >
        <cl-form-group>
          <cl-form-label
            :label-for="'name'"
            class="hover:tw-cursor-pointer tw-mr-4"
          >
            {{ $t("company-name-tab") }}
          </cl-form-label>
          <cl-form-input
            :id="'name'"
            :name="'name'"
            :type="'text'"
            :placeholder="$t('company-name-placeholder')"
            :data-test-id="'msp-branding-input-company-name'"
            v-model:value="form.name"
            :state="!v$.name.$dirty || !isDataLoaded ? null : !v$.name.$error"
            @input="handleNameInput"
            :readonly="
              !isWriteAllowed ||
              (authUserRole === 'admin' && authUserLevel === 'msp')
            "
          />
          <div class="tw-flex tw-flex-row tw-justify-end" v-if="isLoading">
            <div class="tw-w-1/4 tw-pt-2">
              <cl-skeleton-row />
            </div>
          </div>
          <div class="tw-flex tw-flex-row" v-else>
            <div class="tw-w-2/3 tw-text-system-error-500 tw-text-sm">
              <span
                class="tw-block"
                v-if="
                  !v$.name.maxLength.$response || !v$.name.minLength.$response
                "
              >
                {{ $t("branding-company-name") }}
              </span>
              <span class="tw-block" v-else-if="!v$.name.required.$response">
                {{ $t("required-field") }}
              </span>
              <span
                class="tw-block"
                v-if="
                  !v$.name.noEmoji.$response ||
                  !v$.name.validNameCharacters.$response
                "
              >
                {{ $t("branding-name-char-type") }}
              </span>
            </div>
            <div class="tw-w-1/3 tw-text-right">
              <span
                :data-test-id="'msp-branding-input-company-name-char-count-hint'"
                class="tw-text-sm"
                :class="{
                  'tw-text-system-error-500':
                    form.name.length > 45 || form.name.length < 3,
                }"
              >
                {{
                  t("character-count", {
                    character_count: form.name.length,
                    max_characters: 45,
                  })
                }}
              </span>
            </div>
          </div>
        </cl-form-group>
        <cl-form-group>
          <div class="tw-flex tw-items-center tw-gap-1">
            <cl-form-label
              :label-for="'email_from'"
              class="hover:tw-cursor-pointer tw-mr-0"
            >
              {{ $t("display-email-address-label") }}
            </cl-form-label>
            <span
              v-tippy="{
                content: $t('display-email-address-tooltip'),
                placement: 'top',
              }"
              class="tw-text-primary-grey-200"
            >
              <cb-icon-information :width="18" />
            </span>
          </div>
          <cl-form-input
            :id="'email_from'"
            :name="'email_from'"
            :type="'email'"
            placeholder=""
            :data-test-id="'msp-branding-input-display-email-address'"
            v-model:value="form.email_from"
            :state="
              !v$.email_from.$dirty || !isDataLoaded
                ? null
                : !v$.email_from.$error
            "
            @input="handleEmailInput"
            :readonly="
              !isWriteAllowed ||
              (authUserRole === 'admin' && authUserLevel === 'msp')
            "
          />
          <div class="tw-flex tw-flex-row tw-justify-end" v-if="isLoading">
            <div class="tw-w-1/4 tw-pt-2">
              <cl-skeleton-row />
            </div>
          </div>
          <div class="tw-flex tw-flex-row" v-else>
            <div class="tw-w-full tw-text-system-error-500 tw-text-sm">
              <span class="tw-block" v-if="!v$.email_from.email.$response">
                {{ $t("valid-email-format") }}
              </span>
            </div>
          </div>
        </cl-form-group>
      </authorisation-wrapper>
    </div>
  </div>
  <div class="tw-flex tw-flex-row tw-gap-2 tw-justify-end">
    <authorisation-wrapper
      :resource="ADMINISTRATORS"
      :action="WRITE"
      v-slot="{ isAllowed: isWriteAllowed }"
    >
      <cl-button
        v-if="isLoading"
        variant="secondary-outline"
        disabled
        :data-test-id="'msp-branding-restore-default-button-loading'"
      >
        <cl-spinner
          :loading-text="$t('loading')"
          :size="'small'"
          class="mr-2"
        />
        <span>{{ $t("restore-default-logo-button") }}</span>
      </cl-button>
      <cl-button
        v-else
        :disabled="!isRestoreBrandingAvailable"
        variant="secondary-outline"
        @click="isRestoreModalVisible = true"
        :data-test-id="'msp-branding-restore-default-button'"
        :readonly="
          !isWriteAllowed ||
          (authUserRole === 'admin' && authUserLevel === 'msp')
        "
      >
        <span>{{ $t("restore-default-logo-button") }}</span>
      </cl-button>
      <cl-button
        v-if="isLoading"
        :variant="'secondary'"
        disabled
        :data-test-id="'msp-branding-apply-button-loading'"
      >
        <cl-spinner
          :loading-text="$t('loading')"
          :size="'small'"
          class="mr-2"
        />
        <span>{{ $t("apply-my-name") }}</span>
      </cl-button>
      <cl-button
        v-else
        variant="secondary"
        :disabled="v$.$invalid || (!v$.name.$dirty && !v$.email_from.$dirty)"
        @click="submit"
        :data-test-id="'msp-branding-apply-my-name-button'"
        :readonly="
          !isWriteAllowed ||
          (authUserRole === 'admin' && authUserLevel === 'msp')
        "
      >
        <span>{{ $t("apply-my-name") }}</span>
      </cl-button>
    </authorisation-wrapper>
  </div>
  <t-delete-confirm
    :visible="isRestoreModalVisible"
    :loading="isLoading"
    @on-close="isRestoreModalVisible = false"
  >
    <template #title>
      <cl-heading :size="'h4'">{{ $t("restore-default-name") }}</cl-heading>
    </template>
    <template #body>
      <div class="tw-relative">
        <cl-p>
          {{ $t("restore-default-company-name") }}
        </cl-p>
      </div>
    </template>
    <template #confirm>
      <cl-button
        variant="danger"
        data-test-id="delete-confirmation-modal-delete"
        @on-click="handleRestore"
      >
        <cl-spinner
          :loading-text="$t('loading')"
          v-if="isLoading"
          :size="'small'"
          class="tw-mr-2"
        />
        <span>{{ t("restore-default-logo-button") }}</span>
      </cl-button>
    </template>
  </t-delete-confirm>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import {
  maxLength,
  minLength,
  required,
  helpers,
  email,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useI18n } from "vue-i18n";
import { ADMINISTRATORS, AuthorisationWrapper, WRITE } from "@/authorisation";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { useBrandingStore } from "@/store/branding.store";
import {
  DEFAULT_BRANDING_NAME,
  DEFAULT_BRANDING_EMAIL,
} from "@/modules/Settings/constants";
import TDeleteConfirm from "@/modals/TDeleteConfirm.vue";
import { trackEvent } from "@/utils/track-event";

const { t } = useI18n();
const authStore = useAuthenticationStore();
const brandingStore = useBrandingStore();
const toastStore = useToastStore();
const isLoading = ref<boolean>(true);
const isRestoreModalVisible = ref<boolean>(false);
const form = ref({
  name: `${DEFAULT_BRANDING_NAME}`,
  email_from: "",
});
const isDataLoaded = ref<boolean>(false);

const emit = defineEmits<{
  (e: "has-changed", value: boolean): void;
}>();

const isRestoreBrandingAvailable = computed<boolean>(() => {
  const formIsSaved = !v$.value.name.$dirty && !v$.value.email_from.$dirty;

  const brandingIsNotDefault = form.value.name !== DEFAULT_BRANDING_NAME;
  const emailIsNotDefault = form.value.email_from !== DEFAULT_BRANDING_EMAIL;

  return formIsSaved && (brandingIsNotDefault || emailIsNotDefault);
});
const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);

const authUser = computed(() => authStore.authUser);
const authUserRole = computed(() => authUser.value?.role);
const authUserLevel = computed(() => authUser.value?.level);

const noEmoji = helpers.withMessage(
  "Emojis are not allowed",
  (value: string) =>
    !/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u.test(value),
);
const allowedCharsRegex = /^[a-zA-Z0-9\-_\&.#'\s]+$/;

const validNameCharacters = helpers.withMessage(
  "Name must have only alphanumeric characters and/or: '- _&.#",
  (value: string) => {
    if (!value) return true;
    return allowedCharsRegex.test(value);
  },
);

const optionalEmail = (value: string) => {
  if (!value || value.trim() === "") return true;
  return email.$validator(value, null, null);
};

const rules = {
  name: {
    required,
    minLength: minLength(3),
    maxLength: maxLength(45),
    noEmoji,
    validNameCharacters,
  },
  email_from: {
    email: helpers.withMessage("Invalid email format", optionalEmail),
  },
};

const v$ = useVuelidate(rules, form);

const handleNameInput = async () => {
  v$.value.name.$touch();
  const isValid = await v$.value.name.$validate();
  if (!isValid) {
    return;
  }
  emit("has-changed", true);
};

const handleEmailInput = async () => {
  v$.value.email_from.$touch();
  emit("has-changed", true);
};

onMounted(async () => {
  isLoading.value = true;
  v$.value.name.$reset();
  v$.value.email_from.$reset();
  const brandingResponse = await brandingStore.getBranding({
    organisationUuid: selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
  });

  if (brandingResponse.name) {
    form.value.name = brandingResponse.name;
    v$.value.name.$reset();
  }
  if (brandingResponse.email_from) {
    form.value.email_from = brandingResponse.email_from;
    v$.value.email_from.$reset();
  }
  emit("has-changed", false);
  isDataLoaded.value = true;
  isLoading.value = false;
});

const submit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    isLoading.value = true;

    await brandingStore.saveBrandingName({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
      name: form.value.name,
      email_from: form.value.email_from || null,
    });
    // Refresh branding after name save
    await brandingStore.getBranding({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });

    toastStore.displayToast({
      title: t("success"),
      message: t("company-name-branding-applied"),
      duration: 5000,
      variant: "success",
      testId: "branding-save-toast-success",
    });
    trackEvent({
      category: "Branding",
      action: "Save name",
      name: "",
    });
    v$.value.name.$reset();
    v$.value.email_from.$reset();
    emit("has-changed", false);
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const handleRestore = async () => {
  try {
    isLoading.value = true;
    const restoreResponse = await brandingStore.restoreName({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });

    form.value.name = DEFAULT_BRANDING_NAME;
    form.value.email_from =
      restoreResponse.email_from || DEFAULT_BRANDING_EMAIL;

    toastStore.displayToast({
      title: t("success"),
      message: t("changes-saved"),
      duration: 5000,
      variant: "success",
      testId: "branding-restore-toast-success",
    });
    trackEvent({
      category: "Branding",
      action: "Restore name",
      name: "",
    });
    v$.value.name.$reset();
    v$.value.email_from.$reset();
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  } finally {
    isLoading.value = false;
    isRestoreModalVisible.value = false;
  }
};
</script>
